/**
 * 변경 검토·적용·삭제 계약 (ADR 0019 · #7)
 * ---------------------------------------------------------------------------
 * index.ts에서 **옮겨 온 dry-run 블록**과 Phase 2(적용·삭제·작업 조회)가 함께
 * 있습니다. 한 결정에 속하는 타입을 한 파일에 모아 두는 것이 목적이고, 소비자는
 * 지금까지처럼 패키지 루트에서 import합니다.
 *
 * 이 모듈이 계약으로 못박는 것:
 *   - 적용 요청에는 매니페스트도 신원도 없습니다. opaque token 하나뿐입니다.
 *   - force·gracePeriodSeconds 같은 필드는 **존재하지 않습니다.**
 *   - 제출 이후에는 취소가 없습니다. 결과를 모르면 작업 조회로 확인합니다.
 */

/* ── 변경 검토 dry-run (ADR 0019 Phase 1) ────────────────────────────────────
   ADR 0018 Explorer 위에 얹는 **검토 전용** 계약입니다.

   적용·삭제·생성·change token·force·범용 write proxy는 **Phase 1 범위에
   없습니다(deferred)** — 기존 Deployment/Secret write 경로는 그대로 보존되고,
   뒤 단계에서 무엇을 되살릴지는 별도 ADR의 결정입니다. 응답에 매니페스트 원문·
   dry-run 객체·Secret 값·Kubernetes Status 원문이 없는 것은 이 계약 자체의
   고정 사항입니다 — raw 매니페스트는 브라우저→BFF 요청 본문에만 존재하고
   응답·오류·로그로 되돌아오지 않습니다.

   정본 경로: POST /api/v1/clusters/{clusterId}/resources/{group}/{version}/{resource}/object/dry-run

   서버가 강제하는 것: 본문 apiVersion·kind·namespace·name·uid·resourceVersion을
   선택 GVR·매니페스트·서버 Scope와 전부 대조, Secret·serviceaccounts는 설정과
   무관하게 거부, 나머지 hard-deny·미등록 GVR·기능 비활성·central 모드는 fail-closed,
   매니페스트는 단일 문서·중복 키 거부, 현재본과 결과 양쪽을 정제한 뒤 유계·결정적
   diff, live GET과 patch 본문 양쪽에서 UID/resourceVersion 재검증. */

/** 검토 대상 하나와 그 대상의 apply configuration. 동사·force·token은 없습니다. */
export interface ResourceDryRunRequest {
  /** 경로 GVR·매니페스트와 정확히 일치해야 합니다. */
  apiVersion: string;
  kind: string;
  /** namespaced면 필수, cluster 범위면 비어야 합니다. 힌트가 아니라 대조 대상입니다. */
  namespace?: string;
  name: string;
  /** 목록에서 본 UID. 다르면 Kubernetes로 나가지 않고 409입니다. */
  uid: string;
  /** CAS 기준. live GET 결과와 대조하고 patch 본문에도 실립니다. */
  resourceVersion: string;
  /** YAML/JSON 단일 문서. 기본 상한 256KiB, 절대 상한 1MiB. 초과 시 413. */
  manifest: string;
}

/** 변경 종류. 색이 아니라 텍스트로 함께 표시합니다. */
export type ResourceDryRunChangeOp = "added" | "removed" | "changed";

/** 정제된 현재본과 정제된 dry-run 결과의 leaf 차이 하나. */
export interface ResourceDryRunChange {
  /** canonical 경로. 배열은 name 키 또는 인덱스입니다. */
  path: string;
  op: ResourceDryRunChangeOp;
  before?: string;
  after?: string;
  /** 정제가 값 비교에서 뺀 경로. 바뀌었다는 사실만 알리고 값은 없습니다. */
  valueRedacted?: boolean;
  /** 값이 512바이트에서 잘렸는지. */
  valueTruncated?: boolean;
}

/**
 * 거절 사유 하나. Kubernetes Status 원문(message·reason·details·causes[].field·
 * causes[].message·code)은 **어느 필드로도 나가지 않습니다.** 서버는 Causes에서
 * 개수와 타입만 읽고 자신이 쓴 고정 문장만 보여 줍니다.
 */
export interface ResourceDryRunViolation {
  /**
   * 거절된 필드 경로. **Phase 1에서는 언제나 비어 있습니다** — causes[].field는
   * 구조적으로 보이지만 서버(특히 admission webhook)가 자유롭게 채우는 문자열이라
   * 객체 값·내부 경로가 실려 올 수 있습니다.
   */
  field?: string;
  /** 서버가 쓴 고정 문장. upstream 문자열이 아닙니다. */
  message: string;
  /**
   * 그 필드를 소유한 fieldManager. **Phase 1에서는 언제나 비어 있습니다** —
   * 소유자 이름이 전용 필드로 오지 않고 사람이 읽는 메시지 안에 섞여 오기
   * 때문입니다. field·manager 모두 신뢰할 수 있는 타입 필드가 생기면 채웁니다.
   */
  manager?: string;
}

/**
 * 검토 결과. `rejected`는 "검토가 성공적으로 끝났고 그 답이 거절"이며,
 * 요청·신원·정책 실패(4xx)와 다릅니다.
 */
export type ResourceDryRunOutcome = "unchanged" | "changed" | "rejected";

/** 거절 주체. force는 어떤 경우에도 지원하지 않습니다. */
export type ResourceDryRunRejectedBy = "validation" | "admission" | "conflict";

export interface ResourceDryRunResponse {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  apiVersion: string;
  kind: string;
  namespace?: string;
  name: string;
  uid: string;
  resourceVersion: string;
  generatedAt: string;
  /** 항상 "k8s-dashboard". 검토와 적용이 공유하는 상수입니다. */
  fieldManager: string;
  outcome: ResourceDryRunOutcome;
  rejectedBy?: ResourceDryRunRejectedBy;
  /**
   * canonical 경로 오름차순 정렬 후 최대 200개. 같은 정제 diff 집합은 항상 같은
   * 순서의 changes를 냅니다 — 응답 전체는 generatedAt 때문에 바이트가 달라집니다.
   */
  changes: ResourceDryRunChange[];
  /** 절단 이전 전체 변경 수. changes.length보다 클 수 있습니다. */
  changeCount: number;
  truncated: boolean;
  /**
   * **서버가 직접 쓴 고정 문장.** API 서버 Warning 헤더의 원문은 어떤 경우에도
   * 들어오지 않습니다 — admission webhook이 쓰는 그 문자열에는 대상 객체의 필드
   * 값이나 정책 세부가 실려 올 수 있습니다. 경고가 있었다는 사실만 전달하며,
   * 개수도 헤더에서 오지 않습니다.
   */
  warnings: string[];
  violations: ResourceDryRunViolation[];
  /** 정제로 비교에서 제외한 경로. 가려졌다는 사실은 보이게 합니다. */
  redacted: string[];
}

/* ── 변경 적용·삭제 (ADR 0019 Phase 2) ───────────────────────────────────────
   검토 바로 옆에 붙는 두 개의 POST와, 그 결과를 다시 묻는 GET 하나입니다.

   정본 경로:
     POST .../object/apply    검토가 발급한 일회용 자격으로 그 변경을 실제 적용
     POST .../object/delete   이름 재입력·전파 정책·precondition을 요구하는 삭제
     GET  .../operations/{requestId}  제출 이후 결과 재조회

   서버가 강제하는 것: 자격은 subject·Scope 지문·cluster·GVR·namespace·name·uid·
   resourceVersion·매니페스트 해시에 묶이고 정확히 한 번 쓰입니다. 유효 시간은
   5분 고정이며 설정으로 늘릴 수 없습니다. 적용은 dryRun 없는 서버사이드 apply
   하나이고 force-conflicts는 켜지지 않습니다. 삭제는 UID/resourceVersion
   precondition을 반드시 싣고 gracePeriodSeconds를 보내지 않습니다. */

/** change token의 계약 길이입니다. 서버가 만든 32바이트 난수의 base64url 표현입니다. */
export const CHANGE_TOKEN_CHARS = 43;

/** 검토 → 적용 사이의 유효 시간(초). **설정 값이 아닙니다.** */
export const CHANGE_TOKEN_TTL_SECONDS = 300;

/** 작업 기록 보존 시간(초). 자격보다 깁니다 — 끊긴 브라우저가 돌아올 시간입니다. */
export const OPERATION_RETENTION_SECONDS = 900;

/**
 * 상관 ID(requestId) 문법입니다. **전 계층이 같은 규칙을 씁니다** —
 * 브라우저가 만든 `X-Request-ID` → 서버 미들웨어의 재사용 판정 → 응답 헤더·본문·
 * 감사 로그 → 작업 기록 저장 키 → `GET .../operations/{requestId}` 경로.
 *
 * 어느 한 계층만 더 좁으면 조용한 불일치가 생깁니다: 서버가 재사용해 응답에 실어
 * 준 ID를 저장 계층이나 조회 경로가 거절하면, 사용자는 ID를 손에 들고도 결과를
 * 영원히 조회하지 못합니다.
 */
export const REQUEST_ID_MAX_LENGTH = 128;
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]+$/;

/** 문자열이 상관 ID 문법에 맞는지 봅니다. 길이와 문자 집합을 함께 봅니다. */
export function isValidRequestId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= REQUEST_ID_MAX_LENGTH &&
    REQUEST_ID_PATTERN.test(value)
  );
}

/**
 * 검토가 발급한 **일회용** 적용 자격입니다.
 *
 * 적용이 켜져 있고, 이 GVR이 적용 opt-in 목록에 있고, 검토 결과가 거절이 아닐 때만
 * 실립니다. 셋 중 하나라도 아니면 필드 자체가 없고 UI는 적용 진입점을 만들지 않습니다.
 */
export interface ResourceChangeGrant {
  /** 서버 상태를 가리키는 불투명 문자열. 매니페스트도 신원도 들어 있지 않습니다. */
  token: string;
  expiresAt: string;
  /** 언제나 CHANGE_TOKEN_TTL_SECONDS입니다. */
  ttlSeconds: number;
}

/** 검토 응답 + 적용 자격. grant가 없으면 이 결과는 적용할 수 없습니다. */
export interface ResourceDryRunEnvelope extends ResourceDryRunResponse {
  grant?: ResourceChangeGrant;
}

/** 적용 한 건. **매니페스트도 신원 필드도 없습니다** — 대상은 token만이 압니다. */
export interface ResourceApplyRequest {
  token: string;
  /** 사용자가 다시 입력한 대상 이름. token이 가리키는 이름과 글자 단위로 같아야 합니다. */
  confirmName: string;
}

/** 적용 결과. 실패는 여기 없습니다(4xx/5xx APIError). */
export type ResourceChangeOutcome = "applied" | "noop";

export interface ResourceApplyResponse {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  apiVersion: string;
  kind: string;
  namespace?: string;
  name: string;
  uid: string;
  /** 검토·CAS 기준이던 값입니다. */
  previousResourceVersion: string;
  /** 적용 뒤의 값. previous와 같으면 outcome은 noop입니다. */
  resourceVersion: string;
  generatedAt: string;
  fieldManager: string;
  outcome: ResourceChangeOutcome;
  /**
   * 감사 로그와 이 응답을 잇는 끈입니다. 브라우저가 응답을 놓쳤을 때 작업 조회에
   * 쓰라고 **본문에도** 싣습니다.
   */
  requestId: string;
  /** 서버가 직접 쓴 고정 문장입니다. API 서버 Warning 원문은 들어오지 않습니다. */
  warnings: string[];
}

/** 삭제 전파 정책. 서버가 기본값을 몰래 고르지 않습니다. */
export type ResourceDeletePropagation = "Foreground" | "Background" | "Orphan";

/** 삭제 한 건. force·gracePeriodSeconds 필드는 존재하지 않습니다. */
export interface ResourceDeleteRequest {
  namespace?: string;
  name: string;
  uid: string;
  resourceVersion: string;
  /** 손으로 다시 입력한 이름. name과 글자 단위로 같아야 합니다. */
  confirmName: string;
  propagationPolicy: ResourceDeletePropagation;
}

/** 삭제 결과. **finalizer를 숨기지 않습니다.** */
export type ResourceDeleteOutcome = "deleted" | "finalizing" | "unconfirmed";

export interface ResourceDeleteResponse {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  apiVersion: string;
  kind: string;
  namespace?: string;
  name: string;
  uid: string;
  /** precondition으로 쓴 값입니다. */
  resourceVersion: string;
  propagationPolicy: ResourceDeletePropagation;
  generatedAt: string;
  outcome: ResourceDeleteOutcome;
  /** 확인 조회에서 남아 있던 finalizer 개수. **이름은 없습니다.** */
  finalizerCount: number;
  requestId: string;
  warnings: string[];
}

/**
 * 작업의 최종 상태. **"취소됨"이 없는 것이 요점입니다** — 제출 이후에는 취소가
 * 존재하지 않고, 확인하지 못한 것은 실패가 아니라 unconfirmed입니다.
 */
export type ResourceOperationState = "succeeded" | "failed" | "unconfirmed";

export type ResourceOperationAction = "apply" | "delete";

/** 작업 한 건의 metadata 전용 요약. 매니페스트·token·Status 원문은 없습니다. */
export interface ResourceOperationResponse {
  clusterId: string;
  requestId: string;
  action: ResourceOperationAction;
  state: ResourceOperationState;
  /** 성공했을 때의 결과 값입니다. */
  outcome?: ResourceChangeOutcome | ResourceDeleteOutcome;
  /** 실패했을 때 **서버 어휘**의 오류 코드입니다. upstream 문자열이 아닙니다. */
  code?: string;
  group?: string;
  version?: string;
  resource?: string;
  namespace?: string;
  name?: string;
  uid?: string;
  resourceVersion?: string;
  finalizerCount?: number;
  startedAt: string;
  finishedAt: string;
}
